// helpers/LocalStorageHelper.js

import { handleError } from "./LogHelpers";

/**
 * Internal helpers
 */

const safeGetUserNoThrow = () => {
  try {
    const storedUser = localStorage.getItem("user");
    return storedUser ? JSON.parse(storedUser) : null;
  } catch {
    return null;
  }
};

const reportStorageError = (prefix, key, error, user = null) => {
  const parsedUser = user ?? safeGetUserNoThrow();
  const msg = `${prefix}: localStorage error (key=${key}) — ${error?.message ?? String(error)}`;

  try {
    handleError(msg, parsedUser);
  } catch {
    // never allow logging failures to cascade
  }

  // eslint-disable-next-line no-console
  console.error(msg, error);
};

/**
 * Basic getters / setters
 */

export const getLocalStorageValue = (key, user = null) => {
  try {
    return localStorage?.getItem?.(key) ?? undefined;
  } catch (error) {
    reportStorageError("LS:get", key, error, user);
    return undefined;
  }
};

// value === undefined → no-op
// value === null      → remove
export const setLocalStorageValue = (key, value, user = null) => {
  try {
    if (value === undefined) return false;

    if (value === null) {
      localStorage?.removeItem?.(key);
      return true;
    }

    let stored;

    const t = typeof value;

    if (t === "string") {
      stored = value;
    } else if (t === "number" || t === "boolean" || t === "bigint") {
      stored = String(value);
    } else if (value instanceof Date) {
      stored = value.toISOString();
    } else if (t === "object") {
      // JSON stringify objects/arrays; throws on circular refs
      stored = JSON.stringify(value);
    } else {
      // symbol, function, etc. -> refuse (prevents silent garbage)
      throw new Error(`Unsupported value type for localStorage: ${t}`);
    }

    localStorage?.setItem?.(key, stored);
    return true;
  } catch (error) {
    reportStorageError("LS:set", key, error, user);
    return false;
  }
};

export const setLocalStorageJson = (key, obj, user = null) => {
  try {
    return setLocalStorageValue(key, obj, user);
  } catch (error) {
    reportStorageError("LS:setJson", key, error, user);
    return false;
  }
};

export const removeLocalStorageValue = (key, user = null) => {
  try {
    localStorage?.removeItem?.(key);
    return true;
  } catch (error) {
    reportStorageError("LS:remove", key, error, user);
    return false;
  }
};

/**
 * Selection token reset
 * Accounts → Projects → Forms → Views
 */

// flags example: { accountToken: true, projectToken: true, formToken: true, viewLabel: true }
export function resetPersistedSelectionTokens(flags = {}, user = null) {
  const {
    accountToken = false,
    projectToken = false,
    formToken = false,
    viewLabel = false,
  } = flags;

  if (accountToken) removeLocalStorageValue("accountToken", user);
  if (projectToken) removeLocalStorageValue("projectToken", user);
  if (formToken) removeLocalStorageValue("formToken", user);
  if (viewLabel) removeLocalStorageValue("viewLabel", user);
}

/**
 * User helpers
 */

export const getUser = () => {
  try {
    const storedUser = localStorage.getItem("user");
    return storedUser ? JSON.parse(storedUser) : null;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error retrieving user from local storage:", error);
    throw new Error("Error retrieving user: " + error.message);
  }
};

/**
 * Instance helpers (single-tab / stale-tab detection)
 */

export const getActiveInstance = () => {
  try {
    const instance = localStorage.getItem("adminSystemInstance");
    return instance ? JSON.parse(instance) : null;
  } catch (error) {
    throw new Error("Error retrieving active instance: " + error.message);
  }
};

export const setNewInstance = () => {
  const newInstanceId = Date.now();

  if (!newInstanceId) {
    // eslint-disable-next-line no-console
    console.error("Invalid new instance ID.");
    throw new Error("Invalid new instance ID.");
  }

  const ok = setLocalStorageJson(
    "adminSystemInstance",
    { id: newInstanceId },
    safeGetUserNoThrow()
  );

  if (!ok) {
    throw new Error("Failed to persist new instance ID to localStorage.");
  }

  return newInstanceId;
};

export const cleanupInstance = (newInstanceId, user = null) => {
  if (!newInstanceId) {
    // eslint-disable-next-line no-console
    console.error("Invalid instance ID for cleanup.");
    throw new Error("Invalid instance ID for cleanup.");
  }

  const currentInstance = getActiveInstance();

  if (currentInstance && currentInstance.id === newInstanceId) {
    removeLocalStorageValue("adminSystemInstance", user ?? safeGetUserNoThrow());
    // eslint-disable-next-line no-console
    console.log("IC: Instance flag removed on unload.");
  }
};

/**
 * Utilities
 */

export const arrayEq = (a, b) =>
  a === b ||
  (Array.isArray(a) &&
    Array.isArray(b) &&
    a.length === b.length &&
    a.every((v, i) => v === b[i]));

/**
 * Active-instance guard (multi-tab protection)
 */

// Enhanced checkExistingInstance for LocalStorageHelpers.js
// This assumes you have these other helper functions available:
// - getUser()
// - getActiveInstance() 
// - removeLocalStorageValue()
// - handleError()

export const checkExistingInstance = () => {
  const timestamp = new Date().toISOString();  
  const parsedUser = getUser();  
  const existingInstance = getActiveInstance();
  
  if (!existingInstance) {
    return -1;
  }

  const instanceTimestamp = existingInstance.id;
  
  const currentTime = Date.now();
  
  const minutesSinceSet = Math.floor((currentTime - instanceTimestamp) / 60000);
  //console.log(`[${timestamp}] CEI: Minutes since instance was set:`, minutesSinceSet);

  const formattedDate = new Intl.DateTimeFormat("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(instanceTimestamp);
  //console.log(`[${timestamp}] CEI: Instance was created at:`, formattedDate);

  if (minutesSinceSet > 360) {
    console.log(`[${timestamp}] CEI: Instance is STALE (>${minutesSinceSet} minutes, threshold is 360)`);
    console.log(`[${timestamp}] CEI: Clearing old instance from localStorage...`);
    
    // eslint-disable-next-line no-console
    console.log(
      `IC: Instance is older than 6 hours (${minutesSinceSet} minutes). Clearing old instance.`
    );

    removeLocalStorageValue("adminSystemInstance", parsedUser);
    
    console.log(`[${timestamp}] CEI: Old instance cleared`);

    const errorMsg =
      `IC: Another instance was opened ${minutesSinceSet} minutes ago, ` +
      `on ${formattedDate}. This is older than 6 hours so the page was reloaded for them`;

    console.log(`[${timestamp}] CEI: Calling handleError with:`, errorMsg);
    handleError(`IC: Check for active instance: ${errorMsg}`, parsedUser);
    
    console.log(`[${timestamp}] CEI: ===== CHECK EXISTING INSTANCE END (stale, cleared) =====`);
    return -1;
  }

  console.log(`[${timestamp}] CEI: Instance is ACTIVE (only ${minutesSinceSet} minutes old, threshold is 360)`);
  
  // eslint-disable-next-line no-console
  console.log(
    `IC: Existing instance detected with ID: ${existingInstance.id}, ` +
    `set ${minutesSinceSet} minutes ago.`
  );

  const errorMsg =
    `IC: Another instance was opened ${minutesSinceSet} minutes previously, ` +
    `on ${formattedDate}. Please close this window and return to the active one.`;

  console.log(`[${timestamp}] CEI: Calling handleError with:`, errorMsg);
  handleError(`IC: Check for active instance: ${errorMsg}`, parsedUser);

  console.log(`[${timestamp}] CEI: ===== CHECK EXISTING INSTANCE END (active instance exists) =====`);
  console.log(`[${timestamp}] CEI: Returning minutes since set:`, minutesSinceSet);
  
  return minutesSinceSet;
};
